import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from 'recharts';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber, formatCurrency } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

const SEGMENT_COLORS = [
    CHART_COLORS.chart.line1,
    CHART_COLORS.chart.line2,
    CHART_COLORS.chart.line3,
    CHART_COLORS.chart.line4,
    CHART_COLORS.chart.line5,
    CHART_COLORS.chart.line6,
    CHART_COLORS.category.advertising,
    CHART_COLORS.category.fees,
    CHART_COLORS.category.shipping,
];

interface FlowSegment {
    name: string;
    value: number;
    fill: string;
}

export const MoneyFlowSankeyChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const { revenueSegments, expenseSegments } = useMemo(() => {
        if (!finance?.breakdown) return { revenueSegments: [] as FlowSegment[], expenseSegments: [] as FlowSegment[] };

        // Revenue sources grouped by channel/entity
        const revBySource: Record<string, { name: string; value: number }> = {};
        const expByCategory: Record<string, { name: string; value: number }> = {};

        for (const item of finance.breakdown) {
            if (item.type === 'REVENUE') {
                const key = (item.entityName as string) || (item.channelId as string) || 'other';
                if (!revBySource[key]) {
                    revBySource[key] = { name: key, value: 0 };
                }
                revBySource[key].value += Number(item.value) || 0;
            } else if (item.type === 'EXPENSE') {
                const cat = (item.category as string) || 'other';
                if (!expByCategory[cat]) {
                    expByCategory[cat] = { name: cat, value: 0 };
                }
                expByCategory[cat].value += Number(item.value) || 0;
            }
        }

        const sortedRevenue = Object.values(revBySource)
            .sort((a, b) => b.value - a.value)
            .slice(0, 8)
            .map((seg, i) => ({
                ...seg,
                name: seg.name.length > 20 ? `${seg.name.slice(0, 20)}...` : seg.name,
                fill: SEGMENT_COLORS[i % SEGMENT_COLORS.length],
            }));

        const totalRevenue = sortedRevenue.reduce((sum, s) => sum + s.value, 0);
        const totalExpenses = Object.values(expByCategory).reduce((sum, s) => sum + s.value, 0);
        const netProfit = totalRevenue - totalExpenses;

        const sortedExpenses = Object.values(expByCategory)
            .sort((a, b) => b.value - a.value)
            .slice(0, 7)
            .map((seg, i) => ({
                ...seg,
                name: formatCategoryLabel(seg.name),
                fill: SEGMENT_COLORS[(i + 3) % SEGMENT_COLORS.length],
            }));

        if (netProfit > 0) {
            sortedExpenses.push({
                name: t('charts.netProfit', 'Net Profit'),
                value: netProfit,
                fill: CHART_COLORS.revenue,
            });
        }

        return { revenueSegments: sortedRevenue, expenseSegments: sortedExpenses };
    }, [finance?.breakdown, t]);

    const isEmpty = useMemo(
        () => revenueSegments.length === 0 && expenseSegments.length === 0,
        [revenueSegments, expenseSegments]
    );

    useSetChartMetadata({ isEmpty });

    // Build stacked bar data: two rows, one for revenue sources, one for expense allocation
    const stackedData = useMemo(() => {
        const revenueRow: Record<string, number | string> = { row: t('charts.revenueSourcesLabel', 'Revenue Sources') };
        for (const seg of revenueSegments) {
            revenueRow[seg.name] = seg.value;
        }

        const expenseRow: Record<string, number | string> = { row: t('charts.costAllocationLabel', 'Cost Allocation') };
        for (const seg of expenseSegments) {
            expenseRow[seg.name] = seg.value;
        }

        return [revenueRow, expenseRow];
    }, [revenueSegments, expenseSegments, t]);

    const allKeys = useMemo(() => {
        const keys = new Set<string>();
        for (const seg of revenueSegments) keys.add(seg.name);
        for (const seg of expenseSegments) keys.add(seg.name);
        return Array.from(keys);
    }, [revenueSegments, expenseSegments]);

    const colorMap = useMemo(() => {
        const map: Record<string, string> = {};
        for (const seg of revenueSegments) map[seg.name] = seg.fill;
        for (const seg of expenseSegments) map[seg.name] = seg.fill;
        return map;
    }, [revenueSegments, expenseSegments]);

    if (isEmpty) return null;

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={stackedData}
                        layout="vertical"
                        margin={{ top: 10, right: 20, bottom: 10, left: 80 }}
                    >
                        <CartesianGrid
                            horizontal={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            type="number"
                            tickFormatter={(v: number) => formatCompactNumber(v, currencySymbol)}
                            {...CHART_STYLES.axis}
                        />
                        <YAxis
                            type="category"
                            dataKey="row"
                            {...CHART_STYLES.axis}
                            width={80}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            itemStyle={CHART_STYLES.tooltip.itemStyle}
                            formatter={(value: number, name: string) => [
                                formatCurrency(value, currencySymbol),
                                name,
                            ]}
                        />
                        <Legend wrapperStyle={{ fontSize: '10px' }} />
                        {allKeys.map((key) => (
                            <Bar
                                key={key}
                                dataKey={key}
                                stackId="flow"
                                fill={colorMap[key]}
                                maxBarSize={60}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

function formatCategoryLabel(category: string): string {
    return category
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
}
